<div class="fixed inset-0 z-40 flex">
  <div class="flex-1 bg-black/30" (click)="closePanel.emit()" aria-hidden="true"></div>

  <aside
    class="w-full max-w-3xl bg-white h-full shadow-2xl overflow-y-auto border-l border-gray-200 flex flex-col"
    appModalDialog
    (dialogClose)="closePanel.emit()"
    role="dialog"
    aria-modal="true"
    aria-label="Repeat Session"
  >
    <!-- Header -->
    <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <div class="flex items-center gap-2">
        <i class="ri-repeat-line text-primary text-lg"></i>
        <h2 class="text-base font-semibold text-gray-800">Repeat Session</h2>
        @if (replay) {
          <span class="text-xs px-2 py-0.5 rounded {{ replayStatusClasses(replay.status) }}">
            {{ replay.status }}
          </span>
        }
      </div>
      <div class="flex items-center gap-2">
        @if (step !== 'plan') {
          <!-- Verbosity toggle, only relevant once we have results -->
          <div class="join" role="tablist" aria-label="Detail level">
            <button
              type="button"
              class="join-item btn btn-xs"
              [class.btn-primary]="verbosity === 'compact'"
              (click)="setVerbosity('compact')"
            >
              Compact
            </button>
            <button
              type="button"
              class="join-item btn btn-xs"
              [class.btn-primary]="verbosity === 'detailed'"
              (click)="setVerbosity('detailed')"
            >
              Detailed
            </button>
            <button
              type="button"
              class="join-item btn btn-xs"
              [class.btn-primary]="verbosity === 'verbose'"
              (click)="setVerbosity('verbose')"
            >
              Verbose
            </button>
          </div>
        }
        <button class="btn btn-xs btn-ghost" (click)="closePanel.emit()" aria-label="Close">
          <i class="ri-close-line text-lg"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 p-4 space-y-4">
      <!-- =========================================================== -->
      <!-- PLAN STEP                                                     -->
      <!-- =========================================================== -->
      @if (step === 'plan') {
        @if (planLoading) {
          <p class="text-sm text-gray-500">Loading replay plan…</p>
        }
        @if (planError) {
          <div class="alert alert-error text-sm">{{ planError }}</div>
        }

        @if (!planLoading && !planError) {
          <!-- Replay parameters (collapsible) -->
          <div class="border border-gray-200 rounded">
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              (click)="paramsExpanded = !paramsExpanded"
            >
              <span class="flex items-center gap-2">
                <i class="ri-settings-3-line"></i>
                Replay parameters
                <span class="text-xs font-normal text-gray-500">
                  shift +{{ dateShiftDays }}d · {{ pacingMode }}
                  @if (pacingMode === 'ffwd') {
                    {{ pacingFFwdMs }}ms
                  }
                  · {{ requestTimeoutSeconds }}s timeout · {{ verbosity }}
                </span>
              </span>
              <i
                [class.ri-arrow-down-s-line]="!paramsExpanded"
                [class.ri-arrow-up-s-line]="paramsExpanded"
              ></i>
            </button>
            @if (paramsExpanded) {
              <div class="px-3 pb-3 pt-1 space-y-3 text-sm">
                <div class="grid grid-cols-2 gap-3">
                  <label class="block">
                    <span class="text-xs text-gray-600">Date shift (days)</span>
                    <input
                      type="number"
                      class="input input-sm input-bordered w-full"
                      [(ngModel)]="dateShiftDays"
                    />
                    <span class="text-xs text-gray-400">
                      Default: today − session.created_at = {{ defaultDateShiftDays }} d
                    </span>
                  </label>

                  <label class="block">
                    <span class="text-xs text-gray-600">Per-request timeout (s)</span>
                    <input
                      type="number"
                      min="1"
                      class="input input-sm input-bordered w-full"
                      [(ngModel)]="requestTimeoutSeconds"
                    />
                  </label>
                </div>

                <div>
                  <span class="text-xs text-gray-600">Pacing</span>
                  <div class="mt-1 flex flex-wrap gap-3">
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="pacing"
                        value="realtime"
                        [(ngModel)]="pacingMode"
                      />
                      Real-time (original gaps)
                    </label>
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="pacing"
                        value="ffwd"
                        [(ngModel)]="pacingMode"
                      />
                      Fast-forward
                    </label>
                    @if (pacingMode === 'ffwd') {
                      <input
                        type="number"
                        min="0"
                        class="input input-xs input-bordered w-24"
                        [(ngModel)]="pacingFFwdMs"
                      />
                      <span class="text-xs text-gray-500 self-center">ms between</span>
                    }
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="pacing"
                        value="sequential"
                        [(ngModel)]="pacingMode"
                      />
                      Sequential (no pause)
                    </label>
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="pacing"
                        value="manual"
                        [(ngModel)]="pacingMode"
                      />
                      Manual (confirm each step)
                    </label>
                  </div>
                </div>

                <div>
                  <span class="text-xs text-gray-600">Detail level</span>
                  <div class="mt-1 flex flex-wrap gap-3">
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="verbosity"
                        value="compact"
                        [(ngModel)]="verbosity"
                      />
                      Compact
                      <span class="text-xs text-gray-400">(status only)</span>
                    </label>
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="verbosity"
                        value="detailed"
                        [(ngModel)]="verbosity"
                      />
                      Detailed
                      <span class="text-xs text-gray-400">(+ snippet + live activity)</span>
                    </label>
                    <label class="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        class="radio radio-xs"
                        name="verbosity"
                        value="verbose"
                        [(ngModel)]="verbosity"
                      />
                      Verbose
                      <span class="text-xs text-gray-400">(+ full I/O from captured replay)</span>
                    </label>
                  </div>
                </div>

                <label class="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-xs"
                    [(ngModel)]="continueOnError"
                  />
                  Continue on error
                </label>
              </div>
            }
          </div>

          @if (nothingReplayable) {
            <!--
              Empty state: every row in this session is non-replayable
              (no raw_requests / no input body pointer). The helper
              buttons and Run replay would silently no-op without this
              banner, which is what reads as "buttons don't work."
              Surfaces the cause clearly and leaves the table below as a
              read-only audit of why each row was rejected.
            -->
            <div class="alert alert-warning text-sm">
              <i class="ri-information-line"></i>
              <span>
                None of this session's requests can be replayed. See the per-row reasons below —
                common causes are a missing
                <code>raw_requests</code>
                envelope (sidecar didn't capture or the upload failed) or a missing
                <code>raw_s3_file</code>
                pointer on the input message (envelope landed but body blob didn't).
              </span>
            </div>
          }

          <!-- Selection helpers -->
          <div class="flex items-center justify-between text-sm">
            <span class="text-gray-600">
              {{ selectedCount }} of {{ replayableCount }} replayable selected
              @if (entries.length > replayableCount) {
                <span class="text-gray-400">({{ entries.length - replayableCount }} skipped)</span>
              }
            </span>
            <div class="flex gap-1">
              <button
                class="btn btn-xs btn-ghost"
                (click)="selectShoppingOnly()"
                [disabled]="nothingReplayable"
              >
                Select shopping
              </button>
              <button
                class="btn btn-xs btn-ghost"
                (click)="selectAllReplayable()"
                [disabled]="nothingReplayable"
              >
                Select all
              </button>
              <button
                class="btn btn-xs btn-ghost"
                (click)="clearSelection()"
                [disabled]="selectedCount === 0"
              >
                Clear
              </button>
            </div>
          </div>

          <!-- Request checklist -->
          <div class="border border-gray-200 rounded overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th class="text-left px-2 py-1.5 w-8"></th>
                  <th class="text-left px-2 py-1.5">Method · Provider</th>
                  <th class="text-left px-2 py-1.5">Captured</th>
                  <th class="text-right px-2 py-1.5">Gap</th>
                  <th class="text-right px-2 py-1.5">Dates</th>
                </tr>
              </thead>
              <tbody>
                @for (e of entries; track e.id) {
                  <tr
                    class="border-t border-gray-100"
                    [class.opacity-50]="!e.replayable"
                    [class.border-l-4]="e.destructive && e.replayable"
                    [class.border-l-red-400]="e.destructive && e.replayable"
                  >
                    <td class="px-2 py-1.5">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-xs"
                        [disabled]="!e.replayable"
                        [(ngModel)]="selected[e.id]"
                      />
                    </td>
                    <td class="px-2 py-1.5">
                      <div class="font-medium text-gray-800">
                        @if (e.destructive && e.replayable) {
                          <i
                            class="ri-alert-line text-red-500"
                            title="Destructive method — opt in carefully"
                          ></i>
                        }
                        {{ e.method }}
                        @if (e.provider_method && e.provider_method !== e.method) {
                          <span class="text-gray-500 text-xs">· {{ e.provider_method }}</span>
                        }
                      </div>
                      @if (e.provider) {
                        <div class="text-xs text-gray-500" [title]="e.provider">
                          {{ e.provider | collapseProviders }}
                        </div>
                      }
                      @if (!e.replayable && e.skip_reason) {
                        <div class="text-xs text-amber-700">{{ e.skip_reason }}</div>
                      }
                      <!--
                        Prerequisite warning — surfaced when this row's
                        method needs a fresh-from-API id (offer / SRID
                        / order) and no earlier replayable row in the
                        plan would produce it. The executor's pre-flight
                        guard would convert this into a hard error at
                        run-time; surfacing it here lets the operator
                        notice and either include the prerequisite or
                        skip the row before clicking Run.
                      -->
                      @if (e.prerequisite_warning) {
                        <div class="text-xs text-amber-700 flex items-start gap-1">
                          <i class="ri-alert-line shrink-0"></i>
                          <span>{{ e.prerequisite_warning }}</span>
                        </div>
                      }
                    </td>
                    <td class="px-2 py-1.5 text-xs text-gray-500">
                      {{ e.created_at | date: 'short' }}
                    </td>
                    <td class="px-2 py-1.5 text-xs text-right text-gray-500">
                      {{ formatGap(e.gap_ms) }}
                    </td>
                    <td class="px-2 py-1.5 text-xs text-right text-gray-500">
                      {{ e.detected_dates }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          @if (startError) {
            <div class="alert alert-error text-sm">{{ startError }}</div>
          }

          <div class="flex items-center justify-end gap-2 pt-2">
            <button
              class="btn btn-sm btn-ghost"
              (click)="repeatCompletely()"
              [disabled]="nothingReplayable"
            >
              <i class="ri-flashlight-line"></i>
              Repeat session completely
            </button>
            <button
              class="btn btn-sm btn-primary"
              (click)="startReplay()"
              [disabled]="selectedCount === 0"
            >
              <i class="ri-play-line"></i>
              Run replay
            </button>
          </div>
        }
      }

      <!-- =========================================================== -->
      <!-- RUNNING / RESULT STEP                                         -->
      <!-- =========================================================== -->
      @if (step === 'running' || step === 'result') {
        <div class="text-sm text-gray-700">{{ summaryLine }}</div>

        @if (replay?.linked_session_value) {
          <!--
            Linked session card — appears as soon as the first replayed
            response carries Ag-Session-Id. Persists for the rest of the
            run (and beyond) so the operator can jump straight to the
            new session the replay produced. Survives partial / failed
            terminal states because the link is recorded eagerly.
          -->
          <div class="alert alert-info text-sm py-2">
            <i class="ri-link"></i>
            <span class="text-sm">
              Linked to new session
              <a
                class="link link-hover font-mono"
                [routerLink]="['/search']"
                [queryParams]="{ q: replay?.linked_session_value }"
              >
                {{ replay?.linked_session_value }}
              </a>
            </span>
          </div>
        }

        @if (manualGate) {
          <!--
            Manual-mode confirmation card. Visible only while the executor
            is parked on a /advance|/skip gate. The countdown reflects the
            backend's per-request manual-await timeout (default 60s); when
            it elapses, the run terminates and we transition to the
            timeout banner below. Skip is destructive-by-intent so it
            optionally captures a free-text reason for the audit trail.
          -->
          <div class="card bg-base-200 border border-amber-300 text-sm">
            <div class="card-body p-3">
              <div class="flex items-center gap-2">
                <i class="ri-pause-circle-line text-amber-600"></i>
                <span class="font-semibold">Awaiting confirmation</span>
                <span class="badge badge-sm badge-outline">{{ manualGate.method }}</span>
                @if (manualGateRemainingSec !== null) {
                  <span class="text-xs text-gray-500 ml-auto">
                    Times out in {{ manualGateRemainingSec }}s
                  </span>
                }
              </div>
              <div class="text-xs text-gray-600">
                Replay is paused on request
                <span class="font-mono">{{ manualGate.requestId }}</span>
                . Click
                <strong>Advance</strong>
                to fire it, or
                <strong>Skip</strong>
                to mark it
                <code>skipped_by_user</code>
                and move on. Skip is the only safe way to bypass a destructive request mid-run.
              </div>
              <div class="flex flex-wrap items-center gap-2 mt-2">
                <input
                  type="text"
                  class="input input-sm input-bordered flex-1 min-w-[12rem]"
                  placeholder="Skip reason (optional)"
                  [(ngModel)]="manualSkipReason"
                />
                <button
                  class="btn btn-sm btn-primary"
                  (click)="advanceCurrentManualStep()"
                  [disabled]="manualActing"
                >
                  <i class="ri-play-line"></i>
                  Advance
                </button>
                <button
                  class="btn btn-sm btn-warning"
                  (click)="skipCurrentManualStep()"
                  [disabled]="manualActing"
                >
                  <i class="ri-skip-forward-line"></i>
                  Skip
                </button>
              </div>
              @if (manualGateError) {
                <div class="text-xs text-red-600 mt-1">{{ manualGateError }}</div>
              }
            </div>
          </div>
        }

        @if (manualTimedOut) {
          <div class="alert alert-error text-sm py-2">
            <i class="ri-time-line"></i>
            <span>
              The replay timed out waiting for confirmation. Re-open the panel to start a fresh run.
            </span>
          </div>
        }

        @for (e of selectedEntries(); track e.id) {
          @let result = resultFor(e);
          @let stepText = stepFor(e);
          <div class="border border-gray-200 rounded">
            <button
              type="button"
              class="w-full flex flex-col items-stretch px-3 py-2 hover:bg-gray-50 text-left"
              (click)="result && toggleExpand(result)"
              [disabled]="!result"
            >
              <div class="flex items-center justify-between gap-2 min-w-0">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="text-xs px-2 py-0.5 rounded {{
                      statusClasses(result?.status || 'pending')
                    }}"
                  >
                    {{ result?.status || 'pending' }}
                  </span>
                  <span class="font-medium text-gray-800 text-sm">{{ e.method }}</span>
                  @if (e.provider) {
                    <span class="text-xs text-gray-500" [title]="e.provider">
                      · {{ e.provider | collapseProviders }}
                    </span>
                  }
                  @if (result?.http_status) {
                    <span class="text-xs text-gray-500">HTTP {{ result?.http_status }}</span>
                  }
                  @if (result?.duration_ms) {
                    <span class="text-xs text-gray-500">{{ result?.duration_ms }}ms</span>
                  }
                </div>
                @if (result) {
                  <i
                    [class.ri-arrow-down-s-line]="!expandedResultIds[result.id]"
                    [class.ri-arrow-up-s-line]="expandedResultIds[result.id]"
                  ></i>
                }
              </div>
              <!--
                Activity-step line — shown only while the row is in
                pre-terminal state (pending/running). Once the row is
                ok/error/skipped the duration + http status convey
                everything; the step text becomes redundant.
              -->
              @if (
                stepText &&
                (result?.status === 'pending' || result?.status === 'running' || !result)
              ) {
                <span class="text-xs text-gray-500 italic mt-0.5">{{ stepText }}</span>
              }
            </button>

            <!-- Expanded body — content depends on verbosity -->
            @if (result && expandedResultIds[result.id]) {
              <div class="px-3 pb-3 pt-1 border-t border-gray-100 space-y-2 text-xs">
                @if (verbosity === 'compact') {
                  <div class="text-gray-500 italic">
                    Switch verbosity to Detailed or Verbose for more.
                  </div>
                }
                @if (verbosity === 'detailed' || verbosity === 'verbose') {
                  @if (result.error) {
                    <div>
                      <div class="font-semibold text-red-700">Error</div>
                      <pre class="bg-red-50 p-2 rounded overflow-x-auto whitespace-pre-wrap">{{
                        result.error
                      }}</pre>
                    </div>
                  }
                  @if (result.response_snippet) {
                    <div>
                      <div class="flex items-center justify-between">
                        <span class="font-semibold text-gray-700">Response (snippet)</span>
                        <button
                          class="btn btn-xs btn-ghost"
                          (click)="copyToClipboard(result.response_snippet || '')"
                        >
                          <i class="ri-clipboard-line"></i>
                          Copy
                        </button>
                      </div>
                      <pre class="bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap">{{
                        result.response_snippet
                      }}</pre>
                    </div>
                  }
                }
                @if (verbosity === 'verbose') {
                  @let io = ioByResultId[result.id];
                  @if (io && asLoading(io)) {
                    <div class="text-gray-500">Loading full I/O…</div>
                  }
                  @if (io && asError(io); as errMsg) {
                    <div class="text-red-700">{{ errMsg }}</div>
                  }
                  @if (io && asIO(io); as fullIo) {
                    @if (fullIo.linked_session_value) {
                      <div class="text-xs text-gray-500">
                        Captured under linked session
                        <span class="font-mono">{{ fullIo.linked_session_value }}</span>
                      </div>
                    }
                    @if (fullIo.request) {
                      <div>
                        <div class="font-semibold text-gray-700">Request</div>
                        @if (fullIo.request.headers) {
                          <details>
                            <summary class="cursor-pointer text-gray-600">Headers</summary>
                            <pre class="bg-gray-50 p-2 rounded overflow-x-auto">{{
                              fullIo.request.headers | json
                            }}</pre>
                          </details>
                        }
                        @if (fullIo.request.body) {
                          <details>
                            <summary class="cursor-pointer text-gray-600">
                              Body
                              @if (fullIo.request.format) {
                                <span class="text-xs text-gray-400">
                                  · {{ fullIo.request.format }}
                                </span>
                              }
                            </summary>
                            <pre
                              class="bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap"
                              >{{ fullIo.request.body }}</pre
                            >
                          </details>
                        }
                      </div>
                    }
                    @if (fullIo.response) {
                      <div>
                        <div class="font-semibold text-gray-700">Response</div>
                        @if (fullIo.response.headers) {
                          <details>
                            <summary class="cursor-pointer text-gray-600">Headers</summary>
                            <pre class="bg-gray-50 p-2 rounded overflow-x-auto">{{
                              fullIo.response.headers | json
                            }}</pre>
                          </details>
                        }
                        @if (fullIo.response.body) {
                          <details open>
                            <summary class="cursor-pointer text-gray-600">
                              Body
                              @if (fullIo.response.format) {
                                <span class="text-xs text-gray-400">
                                  · {{ fullIo.response.format }}
                                </span>
                              }
                            </summary>
                            <pre
                              class="bg-gray-50 p-2 rounded overflow-x-auto whitespace-pre-wrap"
                              >{{ fullIo.response.body }}</pre
                            >
                          </details>
                        }
                      </div>
                    }
                  }
                }
              </div>
            }
          </div>
        }

        <!--
          Replay log — append-only operational trace, paste-able into
          tickets / chat. Visible during the run AND once it's finished
          so operators can grab the final form after the executor
          settles. Payloads/headers/snippets are intentionally excluded;
          this is "what fired and how it ended", not "what was in the
          envelope". Use the verbose I/O view for payload-level debug.
        -->
        @if (replayLog.length) {
          <div class="border border-gray-200 rounded-lg overflow-hidden">
            <div
              class="bg-gray-50 px-3 py-1.5 flex items-center justify-between border-b border-gray-200"
            >
              <div class="flex items-center gap-2">
                <i class="ri-terminal-line text-gray-500"></i>
                <h3 class="text-sm font-semibold text-gray-700">Replay log</h3>
                <span class="text-xs text-gray-500">
                  {{ replayLog.length }} line{{ replayLog.length === 1 ? '' : 's' }} · no payloads
                </span>
              </div>
              <button
                type="button"
                class="btn btn-xs btn-ghost"
                (click)="copyLog()"
                [disabled]="!replayLog.length"
                title="Copy the full log to the clipboard"
              >
                @if (copiedLog) {
                  <i class="ri-check-line text-green-600"></i>
                  Copied
                } @else {
                  <i class="ri-clipboard-line"></i>
                  Copy
                }
              </button>
            </div>
            <pre
              class="m-0 p-3 text-xs font-mono leading-tight bg-white overflow-auto max-h-72 whitespace-pre"
            >@for (line of replayLog; track $index) {<span [ngClass]="logLineClasses(line.level)">[{{ line.ts }}] {{ line.level | uppercase }}  {{ line.msg }}
</span>}</pre>
          </div>
        }

        @if (step === 'result') {
          <!--
            Run again — resets per-run state (replay, results, log,
            in-flight subscriptions) and drops back to the Plan step.
            The operator's selection AND parameters are preserved so
            iterate-on-failure stays one click + tweak away. See
            runAgain() for the full reset rationale.
          -->
          <div class="flex justify-between items-center pt-2">
            <span class="text-xs text-gray-500">
              Replay finished — tweak parameters or selection and run another attempt.
            </span>
            <button class="btn btn-sm btn-primary" (click)="runAgain()">
              <i class="ri-restart-line mr-1"></i>
              Run again
            </button>
          </div>
        }
      }
    </div>
  </aside>
</div>
